/**
 * =============================================================================
 * IDS CRM Web - Pick the engineers on a job
 * =============================================================================
 *
 * Gareth 2026-09-14, on the tick-box chips for "Other engineers on this job":
 * "would be better as a drop down selection menu with a add button. There
 * would then be a list of engineers on the job then have an 'x' or remove
 * button next to each."
 *
 * A dropdown of the engineers not yet added, an Add button, and the chosen
 * engineers listed underneath, each with a remove button. The dropdown only
 * offers people who are not already listed, so nobody can be added twice.
 * Used by Log Work (co-engineers) and Change crew, so the two read the same.
 *
 * Several engineers on one job (Phase 5): Schedule Engineer Work uses it for
 * the crew. The first engineer on the list is the lead. Three optional props
 * serve that page, and with none of them a row renders exactly as before:
 *   leadLabel  - a badge on the first row (e.g. 'Lead');
 *   noteFor    - something shown under a name (the availability warnings);
 *   onMakeLead - a "Make lead" button on every row but the first.
 * =============================================================================
 */

import React, { useState } from 'react';
import './EngineerPicker.css';

/**
 * The list with one id moved to the front, the rest keeping their order.
 * What a "Make lead" press does to a crew.
 */
export function moveToFront(ids, userId) {
  const list = Array.isArray(ids) ? ids : [];
  if (!list.includes(userId)) return list;
  return [userId, ...list.filter(entry => entry !== userId)];
}

/**
 * @param {object} props
 * @param {string} props.id - id for the dropdown, so a <label htmlFor> names it
 * @param {Array<{user_id: string, full_name: string}>} props.options - who can be added
 * @param {string[]} props.selectedIds - who is on the list, in the order added
 * @param {(ids: string[]) => void} props.onChange
 * @param {Object<string, string>} [props.knownNames] - names for listed people
 *        who are not in options (e.g. someone on the crew no longer active)
 * @param {string} [props.emptyText]
 * @param {string} [props.leadLabel] - badge shown on the first row
 * @param {(userId: string) => React.ReactNode|null} [props.noteFor] - shown under that name
 * @param {(userId: string) => void} [props.onMakeLead] - called by a row's
 *        "Make lead" button; the page moves that id to the front (moveToFront)
 */
function EngineerPicker({
  id,
  options,
  selectedIds,
  onChange,
  knownNames = {},
  emptyText = 'Nobody else added yet.',
  leadLabel,
  noteFor,
  onMakeLead
}) {
  const [choice, setChoice] = useState('');
  const selected = Array.isArray(selectedIds) ? selectedIds : [];
  const engineers = Array.isArray(options) ? options : [];
  const nameOf = (userId) => {
    const engineer = engineers.find(entry => entry.user_id === userId);
    return (engineer && engineer.full_name) || knownNames[userId] || 'Unknown engineer';
  };
  const available = engineers.filter(engineer => !selected.includes(engineer.user_id));
  // A choice that has since been added, or has gone from the options, cannot be added.
  const choiceValid = available.some(engineer => engineer.user_id === choice);

  const add = () => {
    if (!choiceValid) return;
    onChange([...selected, choice]);
    setChoice('');
  };

  const remove = (userId) => onChange(selected.filter(entry => entry !== userId));

  // Only a page that asks for a lead badge, a note or Make lead gets the
  // richer row; everyone else (tickets, Log Work) keeps the plain one.
  const richRows = Boolean(leadLabel || typeof noteFor === 'function' || typeof onMakeLead === 'function');

  const removeButton = (userId, name) => (
    <button
      type="button"
      className="engineer-picker-remove"
      onClick={() => remove(userId)}
      aria-label={`Remove ${name}`}
      title={`Remove ${name}`}
    >
      <span aria-hidden="true">×</span>
    </button>
  );

  const richRow = (userId, name, index) => {
    const note = typeof noteFor === 'function' ? noteFor(userId) : null;
    const canMakeLead = typeof onMakeLead === 'function' && index > 0;
    return (
      <li key={userId} className="engineer-picker-item engineer-picker-item-rich">
        <div className="engineer-picker-main">
          <span className="engineer-picker-name">
            {name}
            {leadLabel && index === 0 && <span className="engineer-picker-lead">{leadLabel}</span>}
          </span>
          {note ? <div className="engineer-picker-note">{note}</div> : null}
        </div>
        {canMakeLead && (
          <button
            type="button"
            className="engineer-picker-make-lead"
            onClick={() => onMakeLead(userId)}
            aria-label={`Make ${name} the lead`}
            title={`Make ${name} the lead`}
          >
            Make lead
          </button>
        )}
        {removeButton(userId, name)}
      </li>
    );
  };

  return (
    <div className="engineer-picker">
      <div className="engineer-picker-row">
        <select
          id={id}
          value={choiceValid ? choice : ''}
          onChange={event => setChoice(event.target.value)}
          disabled={available.length === 0}
        >
          <option value="">{available.length === 0 ? 'Everyone has been added' : 'Choose an engineer…'}</option>
          {available.map(engineer => (
            <option key={engineer.user_id} value={engineer.user_id}>{engineer.full_name}</option>
          ))}
        </select>
        <button type="button" className="engineer-picker-add" onClick={add} disabled={!choiceValid}>
          Add
        </button>
      </div>

      {selected.length === 0 ? (
        <p className="engineer-picker-empty">{emptyText}</p>
      ) : (
        <ul className="engineer-picker-list" aria-label="Engineers added">
          {selected.map((userId, index) => {
            const name = nameOf(userId);
            if (richRows) return richRow(userId, name, index);
            return (
              <li key={userId} className="engineer-picker-item">
                <span className="engineer-picker-name">{name}</span>
                {removeButton(userId, name)}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default EngineerPicker;
